import React, { useEffect, useReducer, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Backdrop,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { Deal } from "../../../validation/customer.validation";
import { getLoginUserData } from "../../../services/account.service";
import {
  createTermSheet,
  getTermSheet,
  updateTermSheet,
} from "../../../services/banker.service";
import { COLLATERAL_OPTIONS, GUARANTY_OPTIONS } from "../../../constants/general.constant";
import {
  TermSheetFormState,
  initialTermSheetState,
  termSheetReducer,
} from "../../../interfaces/termSheet.interface";

interface Props {
  open: boolean;
  deal: Partial<Deal> | null;
  docxUrl?: string;
  onClose: () => void;
  onAccept: (dealId: string) => Promise<void>;
}

const TermsSheet: React.FC<Props> = ({
  open,
  deal,
  docxUrl,
  onClose,
  onAccept,
}) => {
  const [state, dispatch] = useReducer(termSheetReducer, initialTermSheetState);

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));


  const setField = useCallback((field: keyof TermSheetFormState, value: any) => {
    dispatch({ type: "SET_FIELD", field, value });
  }, []);

  const setError = useCallback((field: keyof TermSheetFormState["errors"], value: string) => {
    dispatch({ type: "SET_ERROR", field, value });
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({ type: "CLEAR_ERRORS" });
  }, []);

  const {
    accepted,
    saving,
    loading,
    existingTermSheet,
    loanType,
    sbaType,
    sbaFee,
    sbaCap,
    facilityType,
    loanAmount,
    tenorYears,
    tenorMonths,
    repaymentSchedule,
    repaymentYears,
    repaymentMonths,
    pricing,
    fees,
    collateral,
    guaranty,
    bankerName,
    errors,
  } = state;

  const {
    loanTypeError,
    sbaTypeError,
    facilityTypeError,
    loanAmountError,
    tenorYearsError,
    tenorMonthsError,
    repaymentYearsError,
    repaymentMonthsError,
    pricingError,
    feesError,
    collateralError,
    guarantyError,
  } = errors;

  const displayTenor = (() => {
    const years = parseInt(tenorYears) || 0;
    const months = parseInt(tenorMonths) || 0;
    const parts = [];
    if (years > 0) parts.push(`${years} years`);
    if (months > 0) parts.push(`${months} months`);
    return parts.join(" ") || "";
  })();

  const displayRepayment = (() => {
    const years = parseInt(repaymentYears) || 0;
    const months = parseInt(repaymentMonths) || 0;
    const parts = [];
    if (years > 0) parts.push(`${years} years`);
    if (months > 0) parts.push(`${months} months`);
    return parts.join(" ") || "";
  })();
  const finalRepaymentSchedule = displayRepayment || repaymentSchedule;
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    const fetchData = async () => {
      setField("loading", true);
      try {
        const user = await getLoginUserData();
        const name = `${(user as any)?.firstName || ""} ${(user as any)?.middleName || ""} ${(user as any)?.lastName || ""}`.trim() || "N/A";
        setField("bankerName", name);

        if (deal?.id) {
          const termSheet = await getTermSheet(deal.id);
          if (termSheet) {
            let tenorYearsVal = "";
            let tenorMonthsVal = "";
            let repaymentYearsVal = "";
            let repaymentMonthsVal = "";

            if (termSheet.tenorMaturity) {
              const yearsMatch = termSheet.tenorMaturity.match(/(\d+)\s*years?/i);
              const monthsMatch = termSheet.tenorMaturity.match(/(\d+)\s*months?/i);
              if (yearsMatch) tenorYearsVal = yearsMatch[1];
              if (monthsMatch) tenorMonthsVal = monthsMatch[1];
            }

            if (termSheet.repaymentSchedule) {
              const repYearsMatch = termSheet.repaymentSchedule.match(/(\d+)\s*years?/i);
              const repMonthsMatch = termSheet.repaymentSchedule.match(/(\d+)\s*months?/i);
              if (repYearsMatch) repaymentYearsVal = repYearsMatch[1];
              if (repMonthsMatch) repaymentMonthsVal = repMonthsMatch[1];
            }

            dispatch({
              type: "LOAD_TERMSHEET",
              payload: {
                existingTermSheet: termSheet,
                loanType: termSheet.loanType === "sba" ? "sba" : "conventional",
                sbaType: termSheet.sbaType || "",
                sbaFee: termSheet.sbaFee || "",
                sbaCap: termSheet.sbaCap || "",
                facilityType: termSheet.facilityType || deal?.loanName || "",
                loanAmount: termSheet.loanAmount || deal?.loanSize?.toString() || "",
                tenorYears: tenorYearsVal,
                tenorMonths: tenorMonthsVal,
                repaymentSchedule: termSheet.repaymentSchedule || "",
                repaymentYears: repaymentYearsVal,
                repaymentMonths: repaymentMonthsVal,
                pricing: termSheet.pricing || "",
                fees: termSheet.fees || "",
                collateral: termSheet.collateral || "",
                guaranty: termSheet.guaranty || "",
              },
            });
          } else {
            dispatch({
              type: "RESET_FORM",
              defaultFacilityType: deal?.loanName || "",
              defaultLoanAmount: deal?.loanSize?.toString() || "",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setField("bankerName", "N/A");
        dispatch({
          type: "RESET_FORM",
          defaultFacilityType: deal?.loanName || "",
          defaultLoanAmount: deal?.loanSize?.toString() || "",
        });
      } finally {
        setField("loading", false);
      }
    };

    if (open) {
      fetchData();
    }

    if (!open) {
      dispatch({
        type: "RESET_FORM",
        defaultFacilityType: deal?.loanName || "",
        defaultLoanAmount: deal?.loanSize?.toString() || "",
      });
      clearErrors();
    }
  }, [open, deal, setField, clearErrors]);

  const validateForm = () => {
    let isValid = true;
    let firstErrorField: any = null;
    clearErrors();

    if (!loanType) {
      setError("loanTypeError", "Loan Type is required");
      if (!firstErrorField) firstErrorField = "loanType";
      isValid = false;
    }

    if (loanType === "sba" && !sbaType) {
      setError("sbaTypeError", "SBA Type is required when SBA is selected");
      if (!firstErrorField) firstErrorField = "sbaType";
      isValid = false;
    }

    if (!facilityType.trim()) {
      setError("facilityTypeError", "Facility Type is required");
      if (!firstErrorField) firstErrorField = "facilityType";
      isValid = false;
    }

    if (!loanAmount.trim()) {
      setError("loanAmountError", "Loan Amount is required");
      if (!firstErrorField) firstErrorField = "loanAmount";
      isValid = false;
    }

    const hasTenor =
      (tenorYears || tenorMonths) &&
      (parseInt(tenorYears || "0") > 0 || parseInt(tenorMonths || "0") > 0);
    if (!hasTenor) {
      setError("tenorYearsError", "Tenor/Maturity is required");
      if (!firstErrorField) firstErrorField = "tenorYears";
      isValid = false;
    }

    const repaymentTenor = (repaymentYears || repaymentMonths) && (parseInt(repaymentYears || "0") > 0 || parseInt(repaymentMonths || "0") > 0);
    if (!repaymentTenor) {
      if (!firstErrorField) firstErrorField = "repaymentSchedule";
      isValid = false;
    }

    const hasRepayment =
      (repaymentYears || repaymentMonths) &&
      (parseInt(repaymentYears || "0") > 0 || parseInt(repaymentMonths || "0") > 0);
    if (!hasRepayment) {
      setError("repaymentYearsError", "Repayment Term is required");
      setError("repaymentMonthsError", "Repayment Term is required");
      if (!firstErrorField) firstErrorField = "repaymentYears";
      isValid = false;
    }

    if (!pricing.trim()) {
      setError("pricingError", "Pricing is required");
      if (!firstErrorField) firstErrorField = "pricing";
      isValid = false;
    }

    if (!fees.trim()) {
      setError("feesError", "Fees is required");
      if (!firstErrorField) firstErrorField = "fees";
      isValid = false;
    }

    if (!collateral.trim()) {
      setError("collateralError", "Collateral is required");
      if (!firstErrorField) firstErrorField = "collateral";
      isValid = false;
    }

    if (!guaranty.trim()) {
      setError("guarantyError", "Guaranty is required");
      if (!firstErrorField) firstErrorField = "guaranty";
      isValid = false;
    }

    if (!isValid && firstErrorField) {
      setTimeout(() => {
        const errorElement = document.querySelector(
          `[data-field="${firstErrorField}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          const inputElement = errorElement.querySelector("input");
          if (inputElement) {
            (inputElement as HTMLInputElement).focus();
          }
        }
      }, 100);
    }

    return isValid;
  };

  const handleAccept = async () => {
    if (!deal?.id) return;
    if (!validateForm()) return;

    setField("saving", true);
    try {
      const user = await getLoginUserData();
      const termSheetData = {
        dealId: deal.id,
        bankerId: user.id,
        customerId: deal.customer?.id,
        loanType: loanType === "sba" ? "sba" : "conventional",
        sbaType: loanType === "sba" ? sbaType : null,
        sbaFee: loanType === "sba" ? sbaFee : null,
        sbaCap: loanType === "sba" ? sbaCap : null,
        facilityType,
        loanAmount,
        tenorMaturity: displayTenor,
        repaymentSchedule: finalRepaymentSchedule,
        pricing,
        fees,
        collateral,
        guaranty,
      };
      if (existingTermSheet) {
        await updateTermSheet(existingTermSheet.id, termSheetData);
      } else {
        await createTermSheet(termSheetData);
      }

      await onAccept(deal.id);
    } catch (error) {
      console.error("Error accepting term sheet:", error);
    } finally {
      setField("saving", false);
      onClose();
    }
  };

  return (
    <>
      {createPortal(
        <Backdrop sx={{ color: "#fff", zIndex: 1400 }} open={loading || saving}>
          <CircularProgress color="primary" />
        </Backdrop>,
        document.body,
      )}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        // Fullscreen on mobile/tablet
        fullScreen={isMobileOrTablet}
        PaperProps={{
          sx: {
            ...(isMobileOrTablet
              ? {
                  height: "100vh",
                  maxHeight: "100vh",
                }
              : {}),
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{ flex: "0 0 auto" }}>Terms Sheet — Review & Accept</DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            pb: 2,
            // Improve iOS/Android scrolling.
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box mb={3}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Date: {today}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {deal?.prospectKyc?.fullName || "N/A"}
            </Typography>
            <Typography variant="body2">
              {deal?.prospectKyc?.companyName || "N/A"}
            </Typography>
            <Typography variant="body2">
              {deal?.prospectKyc?.businessStreet || "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {deal?.prospectKyc?.businessCity || "N/A"}{" "}
              {deal?.prospectKyc?.businessState || ""}{" "}
              {deal?.prospectKyc?.businessZipcode || ""}
            </Typography>
            <Typography variant="body2">
              Thank you for your interest in financing with Fifth Third Bank,
              National Association ("Fifth Third"). We are pleased to provide
              you this Term Sheet that summarizes the basic terms and conditions
              for consideration relative to the proposed financing ("Term
              Sheet"):
            </Typography>
          </Box>


          <Divider sx={{ my: 2 }} />

          {/* Section: Summary Terms and Conditions for Discussion */}
          <Typography variant="h6" gutterBottom>
            Summary Terms and Conditions for Discussion
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Borrower(s)
                  </TableCell>
                  <TableCell>
                    {deal?.prospectKyc?.companyName || "N/A"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Guarantor(s)
                  </TableCell>
                  <TableCell>{deal?.prospectKyc?.fullName || "N/A"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Facility Type
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      value={facilityType}
                      onChange={(e) => setField("facilityType", e.target.value)}
                      error={!!facilityTypeError}
                      size="small"
                      variant="standard"
                      placeholder="Enter facility type"
                      inputProps={{ "data-field": "facilityType" }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Loan Amount
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="text"
                      value={loanAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          setField("loanAmount", value.replace(/[^0-9.]/g, ""));
                        }
                      }}
                      error={!!loanAmountError}
                      size="small"
                      variant="standard"
                      placeholder="Enter loan amount"
                      InputProps={{
                        startAdornment: "$",
                      }}
                      inputProps={{ "data-field": "loanAmount" }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Tenor/Maturity
                  </TableCell>
                  <TableCell>
                    <Grid container spacing={1} alignItems="center">
                      <Grid size={6}>
                        <FormControl
                          fullWidth
                          error={!!tenorYearsError}
                          size="small"
                          data-field="tenorYears"
                        >
                          <InputLabel>Years</InputLabel>
                          <Select
                            value={tenorYears || ""}
                            label="Years"
                            onChange={(e) =>
                              setField("tenorYears", e.target.value as string)
                            }
                          >
                            {Array.from({ length: 51 }, (_, i) => (
                              <MenuItem key={i} value={i.toString()}>
                                {i === 0
                                  ? "0 years"
                                  : `${i} year${i !== 1 ? "s" : ""}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {tenorYearsError && (
                            <Typography variant="caption" color="error">
                              {tenorYearsError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid size={6}>
                        <FormControl
                          fullWidth
                          error={!!tenorMonthsError}
                          size="small"
                          data-field="tenorMonths"
                        >
                          <InputLabel>Months</InputLabel>
                          <Select
                            value={tenorMonths || ""}
                            label="Months"
                            onChange={(e) =>
                              setField("tenorMonths", e.target.value as string)
                            }
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <MenuItem key={i} value={i.toString()}>
                                {i === 0
                                  ? "0 months"
                                  : `${i} month${i !== 1 ? "s" : ""}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {tenorMonthsError && (
                            <Typography variant="caption" color="error">
                              {tenorMonthsError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Repayment Schedule
                  </TableCell>
                  <TableCell>
                    <Grid container spacing={1}>
                      <Grid size={6}>
                        <FormControl
                          fullWidth
                          error={!!repaymentYearsError}
                          size="small"
                          data-field="repaymentYears"
                        >
                          <InputLabel>Years</InputLabel>
                          <Select
                            value={repaymentYears || ""}
                            label="Years"
                            onChange={(e) =>
                              setField("repaymentYears", e.target.value as string)
                            }
                          >
                            {Array.from({ length: 51 }, (_, i) => (
                              <MenuItem key={i} value={i.toString()}>
                                {i === 0
                                  ? "0 years"
                                  : `${i} year${i !== 1 ? "s" : ""}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {repaymentYearsError && (
                            <Typography variant="caption" color="error">
                              {repaymentYearsError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid size={6}>
                        <FormControl
                          fullWidth
                          error={!!repaymentMonthsError}
                          size="small"
                          data-field="repaymentMonths"
                        >
                          <InputLabel>Months</InputLabel>
                          <Select
                            value={repaymentMonths || ""}
                            label="Months"
                            onChange={(e) =>
                              setField("repaymentMonths", e.target.value as string)
                            }
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <MenuItem key={i} value={i.toString()}>
                                {i === 0
                                  ? "0 months"
                                  : `${i} month${i !== 1 ? "s" : ""}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {repaymentMonthsError && (
                            <Typography variant="caption" color="error">
                              {repaymentMonthsError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Pricing
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="text"
                      value={pricing}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          setField("pricing", value.replace(/[^0-9.]/g, ""));
                        }
                      }}
                      error={!!pricingError}
                      size="small"
                      variant="standard"
                      placeholder="e.g., 5.60"
                      InputProps={{
                        endAdornment: "%",
                      }}
                      inputProps={{ "data-field": "pricing" }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Fees
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="text"
                      value={fees}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          setField("fees", value);
                        }
                      }}
                      error={!!feesError}
                      size="small"
                      variant="standard"
                      placeholder="e.g., 2400.00"
                      inputProps={{ "data-field": "fees" }}
                      InputProps={{
                        startAdornment: "$",
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Collateral
                  </TableCell>
                  <TableCell>
                    <FormControl
                      fullWidth
                      error={!!collateralError}
                      size="small"
                      data-field="collateral"
                    >
                      <Select
                        value={collateral}
                        onChange={(e) => setField("collateral", e.target.value as string)}
                        displayEmpty
                        variant="standard"
                      >
                        {COLLATERAL_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option || "Select collateral..."}
                          </MenuItem>
                        ))}
                      </Select>
                      {collateralError && (
                        <Typography variant="caption" color="error">
                          {collateralError}
                        </Typography>
                      )}
                    </FormControl>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Guaranty
                  </TableCell>
                  <TableCell>
                    <FormControl
                      fullWidth
                      error={!!guarantyError}
                      size="small"
                      data-field="guaranty"
                    >
                      <Select
                        value={guaranty}
                        onChange={(e) => setField("guaranty", e.target.value as string)}
                        displayEmpty
                        variant="standard"
                      >
                        {GUARANTY_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option || "Select guaranty..."}
                          </MenuItem>
                        ))}
                      </Select>
                      {guarantyError && (
                        <Typography variant="caption" color="error">
                          {guarantyError}
                        </Typography>
                      )}
                    </FormControl>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Financial Covenant(s)
                  </TableCell>
                  <TableCell>
                    Minimum Fixed Charge Coverage covenant of{" "}
                    {deal?.minDSCRatio || "N/A"}x
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Reporting Requirements
                  </TableCell>
                  <TableCell>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 2, listStyleType: "disc" }}
                    >
                      <li>
                        Annual Company Tax returns for all companies within 120
                        days of the year end.
                      </li>
                      <li>
                        Personal tax returns within 120 days of the calendar
                        year end.
                      </li>
                      <li>
                        Annual Personal financial statements of all guarantors
                        within 120 days of calendar year end
                      </li>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Other Expenses
                  </TableCell>
                  <TableCell>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 2, listStyleType: "disc" }}
                    >
                      <li>Appraisal</li>
                      <li>Title</li>
                      <li>3rd party fees</li>
                      <li>Other fees potential review</li>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Additional Bank Services
                  </TableCell>
                  <TableCell>
                    Borrower shall maintain its primary cash management services
                    and deposit accounts with Fifth Third
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />

          {/* Loan Details Input Fields */}
          <Box mt={3} mb={2}>
            <Typography variant="h6" gutterBottom>
              Loan Details
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl
                  fullWidth
                  required
                  error={!!loanTypeError}
                  data-field="loanType"
                >
                  <InputLabel>Loan Type</InputLabel>
                  <Select
                    value={loanType}
                    label="Loan Type"
                    onChange={(e) => setField("loanType", e.target.value)}
                  >
                    <MenuItem value="conventional">Conventional</MenuItem>
                    <MenuItem value="sba">SBA</MenuItem>
                  </Select>
                  {loanTypeError && (
                    <Typography variant="caption" color="error">
                      {loanTypeError}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {loanType === "sba" && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth required error={!!sbaTypeError}>
                      <InputLabel>SBA Type</InputLabel>
                      <Select
                        value={sbaType}
                        label="SBA Type"
                        onChange={(e) => setField("sbaType", e.target.value)}
                      >
                        <MenuItem value="7a">7A</MenuItem>
                        <MenuItem value="504">504</MenuItem>
                        <MenuItem value="express">SBA Express</MenuItem>
                      </Select>
                      {sbaTypeError && (
                        <Typography variant="caption" color="error">
                          {sbaTypeError}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="SBA % Fee"
                      value={sbaFee}
                      onChange={(e) => setField("sbaFee", e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="CAP $ amount"
                      value={sbaCap}
                      onChange={(e) => setField("sbaCap", e.target.value)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Please Note/Disclaimer and Confidentiality */}
          <Typography variant="h6" gutterBottom>
            Important Notes and Disclaimer
          </Typography>

          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            Please note: This Term Sheet is not a commitment to enter or
            consummate any transaction, or to take or refrain from taking any
            other action. It is for discussion purposes only. Discussions may be
            terminated at any time without liability to, or obligation of, Fifth
            Third. Fifth Third will be deemed to have made a commitment only if
            it enters a written commitment letter signed by an authorized
            officer of Fifth Third. No other representations, whether expressed
            or implied, may be understood to bind or obligate Fifth Third, and
            circulation of draft loan documents for discussion or review shall
            not constitute a commitment to lend on any terms.
          </Typography>

          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            Confidentiality
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This Term Sheet is confidential information subject to any
            applicable confidentiality or nondisclosure agreement. If there is
            no applicable confidentiality or nondisclosure agreement then you
            will, during the discussions contemplated by this Term Sheet and
            thereafter, hold this Term Sheet and its contents in confidence and
            will not disclose them to any person or entity (except your
            directors, officers, employees, legal counsel, and financial
            representatives with a bona fide need to know) without Fifth Third's
            prior written consent.
          </Typography>

          <Box mt={3} textAlign="left">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Sincerely,
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              FIFTH THIRD BANK
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {bankerName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Vice President Middle Market Relationship Manager
            </Typography>
            <Typography variant="body2">847-271-4157</Typography>
          </Box>

          {/* Section: Acceptance Checkbox */}
          <Box mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setField("accepted", e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I have read and agree to the term sheet and authorize sharing
                  my deal details.
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            flex: "0 0 auto",
            px: 3,
            py: 2,
            position: isMobileOrTablet ? "sticky" : "static",
            bottom: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            color="error"
            disabled={saving}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!accepted || saving}
            onClick={handleAccept}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
            }}
            startIcon={
              saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            Accept & Continue
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default TermsSheet;

